// Admin dashboard — platform health KPIs. Every card is a live count from a list endpoint's
// `total` (per_page=1): new sign-ups (?days=7), active users (?is_active=true) and total users from
// /admin/users; companies / countries / ledger categories from their own list endpoints.
import { html, signal, navigate } from '../shared/t4.js';
import { icon } from '../shared/icons.js';
import { api } from '../shared/api.js';
import { shell } from './shell.js';
import { isPartnerAdmin, isPlatformAdmin, partnerRegionName } from '../shared/auth.js';

// #2 — quick jump to the most-used screens
const QUICK_LINKS = [
  { label: 'Countries',        sub: 'Regions, ledger codes & pricing', icon: '◍', path: '/countries' },
  { label: 'Ledger categories', sub: 'IFRS classification',            icon: '▤', path: '/categories' },
  { label: 'Languages',        sub: 'Locales & word references',       icon: '⌘', path: '/languages' },
  { label: 'Companies',        sub: 'Tenant organisations',            icon: '▢', path: '/companies' },
];
// A Regional Partner's quick links point only at their scoped screens.
const PARTNER_QUICK_LINKS = [
  { label: 'Companies', sub: 'Your region', icon: '▢', path: '/companies' },
  { label: 'Users',     sub: 'Your region', icon: '⍜', path: '/users' },
  { label: 'Billing',   sub: 'Your region', icon: '▦', path: '/billing' },
];

export function dashboardScreen(){
  const regional = isPartnerAdmin() && !isPlatformAdmin();   // Regional Partner — scoped dashboard
  const quickLinks = regional ? PARTNER_QUICK_LINKS : QUICK_LINKS;
  const stats = signal({});          // key -> number | null
  const ready = signal(false);

  async function count(path, extra){
    const qs = '?per_page=1' + (extra ? '&' + extra : '');
    try { const r = await api.get(path + qs); return (r && r.total) || 0; }
    catch { return null; }
  }
  (async () => {
    // Users/companies counts are region-scoped by the API for a Regional Partner. Countries + ledger
    // categories are global reference — only fetched/shown for a global admin.
    const base = await Promise.all([
      count('/admin/users', 'days=7'), count('/admin/users', 'is_active=true'),
      count('/admin/users'), count('/admin/companies'),
    ]);
    const [signups7d, activeUsers, users, companies] = base;
    const [countries, categories] = regional ? [null, null]
      : await Promise.all([count('/admin/countries'), count('/admin/ledger-categories')]);
    stats.value = { signups7d, activeUsers, users, companies, countries, categories };
    ready.value = true;
  })();

  const fmt = v => (v === null || v === undefined) ? '—' : String(v);

  const cards = () => {
    const s = stats.value;
    const base = [
      { label:'New sign-ups',      val:fmt(s.signups7d),   sub:'last 7 days',            accent:'success' },
      { label:'Active users',      val:fmt(s.activeUsers), sub:'can sign in',            accent:'primary' },
      { label:'Users',             val:fmt(s.users),       sub:'signed up',              accent:'info' },
      { label:'Companies',         val:fmt(s.companies),   sub:'tenants',                accent:'primary' },
    ];
    if(regional) return base;   // global reference cards are not a Regional Partner's concern
    return [...base,
      { label:'Countries',         val:fmt(s.countries),   sub:'active regions',         accent:'info' },
      { label:'Ledger categories', val:fmt(s.categories),  sub:'global · all countries', accent:'dark' },
    ];
  };

  const content = html`
    <div class="page">
      <div class="page-head">
        <h1 class="page-title">Dashboard</h1>
        <div class="page-sub">${regional ? (partnerRegionName() || 'Your region') + ' · partner overview' : 'Platform health at a glance'}</div>
      </div>
      <div class="quick-grid">
        ${quickLinks.map(l => html`
          <button type="button" class="quick-card" @click=${() => navigate(l.path)}>
            <span class="quick-ico">${l.icon}</span>
            <span class="quick-text"><b>${l.label}</b><span class="quick-sub">${l.sub}</span></span>
            <span class="row-chevron">${icon('chevron-right', {size: 14})}</span>
          </button>`)}
      </div>
      <div class="kpi-grid">
        ${() => cards().map(c => html`
          <div class=${'kpi accent-' + c.accent}>
            <div class="card-body">
              <div class="kpi-label">${c.label}</div>
              <div class="kpi-val">${c.val}</div>
              <div class="kpi-sub">${c.sub}</div>
            </div>
          </div>`)}
      </div>
    </div>`;

  return shell('dashboard', 'Dashboard', content);
}
